import styled from 'styled-components'
import { useCallback } from 'react'

import { Direction, SortBy, LpVaultListRow } from '../types'
import { Typography } from 'components/Typography/Typography'
import { IconArrowUp } from 'components/Icons/IconArrowUp'
import { IconArrowDown } from 'components/Icons/IconArrowDown'
import { LpVaultRow } from './LpVaultRow'

interface Props {
  displayData: LpVaultListRow[]
  sortConfig: { key: SortBy | null; direction: Direction | null }
  onColumnHeaderClick: (key: SortBy) => void
  isLoading: boolean
}

const VaultsHeader = styled.div<{ $isEmpty?: boolean }>`
  display: flex;
  padding: 24px 32px;
  position: relative;
  z-index: 1;
  border-bottom: ${p => (p.$isEmpty ? 'none' : `2px solid ${p.theme.colors.listHeaderBorder};`)}
  gap: 20px;
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    display: none;
  }
`

const Th = styled(Typography)<{ onClick?: () => void }>`
  flex-basis: 200px;
  cursor: pointer;
  display: flex;
  align-items: center;
  z-index: 10;
  & > div > svg {
    margin-left: 8px;
    & > path {
      stroke: ${p => p.theme.colors.text.primary};
    }
  }
`

const VaultTable = styled.div`
  border: 0.7px solid ${p => p.theme.colors.divider};
  background: ${p => p.theme.colors.background.primary};
  border-radius: 16px;
  @media screen and (min-width: ${p => p.theme.breakpoints.md}px) {
    box-shadow: 0px 0px 16px 0px ${p => p.theme.colors.background.hover};
    border-radius: 32px;
  }
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    border: none;
  }
`

export const LpVaultsList = ({ displayData, sortConfig, onColumnHeaderClick, isLoading }: Props) => {
  const getSortIndicator = useCallback(
    (key: SortBy) => {
      if (sortConfig.key === key) {
        return sortConfig.direction === Direction.Ascending ? <IconArrowUp /> : <IconArrowDown />
      }
      return null
    },
    [sortConfig.direction, sortConfig.key]
  )

  return (
    <VaultTable>
      <VaultsHeader $isEmpty={displayData.length === 0}>
        <Th variant="label1" fontWeight={sortConfig.key === SortBy.Vault ? 'semiBold' : 'regular'}>
          <div onClick={() => onColumnHeaderClick(SortBy.Vault)}>
            Vault
            {getSortIndicator(SortBy.Vault)}
          </div>
        </Th>
        <Th variant="label1" fontWeight={sortConfig.key === SortBy.Tvl ? 'semiBold' : 'regular'}>
          <div onClick={() => onColumnHeaderClick(SortBy.Tvl)}>
            TVL
            {getSortIndicator(SortBy.Tvl)}
          </div>
        </Th>
        <Th variant="label1">
          <div style={{ cursor: 'auto' }}>
            APR
            {getSortIndicator(SortBy.Apr)}
          </div>
        </Th>
        <Th variant="label1" fontWeight={sortConfig.key === SortBy.Deposited ? 'semiBold' : 'regular'}>
          <div onClick={() => onColumnHeaderClick(SortBy.Deposited)}>
            Deposited
            {getSortIndicator(SortBy.Deposited)}
          </div>
        </Th>
        <Th variant="label1" fontWeight={sortConfig.key === SortBy.Earned ? 'semiBold' : 'regular'}>
          <div onClick={() => onColumnHeaderClick(SortBy.Earned)}>
            Earned
            {getSortIndicator(SortBy.Earned)}
          </div>
        </Th>
      </VaultsHeader>
      <div>
        {displayData.map((info, index) => (
          <LpVaultRow
            key={`${info.coinX.symbol}-${info.coinY.symbol}`}
            displayInfo={info}
            isLast={index === displayData.length - 1}
            isLoading={isLoading}
          />
        ))}
      </div>
    </VaultTable>
  )
}
